<template>
  <form
    @submit.prevent="form.transform((data) => ({
      client_id: data.client?.id ?? null,
      project_id: data.project?.id ?? null,
      title: data.title,
      subtitle: data.subtitle,
      range: data.range,
      notes: data.notes,
      pax: data.pax,
      currency_id: currency?.id ?? null,
      amount: data.amount,
      destinations: data.destinations ?? [],
    })).post(route('proposals.store'), {
      onError: () => {},
    })">
    <article class="bg-white">
      <header class="bg-indigo-200 px-5 py-8">
        <div class="flex items-center space-x-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="size-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>

          <h2 class="text-lg font-bold">
            Create new proposal
          </h2>
        </div>
      </header>

      <main class="max-w-3xl p-8">
        <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4">
          <label class="flex flex-col space-y-2 text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">
            <span>Destinations</span>
            <small>First listed will be the main destination. Add others for access to their entities.</small>
          </label>
          <div class="mt-2 sm:col-span-2 sm:mt-0">
            <ComboboxWithSecondaryText
              v-model="form.destinations"
              class="mt-2"
              has-multiple
              :disabled="availableDestinations.length < 1"
              :placeholder="availableDestinations.length > 0 ? 'Search for or select destinations' : 'There are no Destinations, please add some'"
              :items="availableDestinations"
              @change="form.clearErrors('destinations')" />
            <p
              v-if="form.errors.destinations"
              class="mt-2 text-sm text-red-600">
              {{ form.errors.destinations }}
            </p>
          </div>
        </div>

        <hr class="my-8">
        <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4">
          <label class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"> Client </label>
          <div class="mt-2 sm:col-span-2 sm:mt-0">
            <DynamicDropdown
              v-model="form.client"
              search-url="/api/clients?search="
              initial-url="/api/clients"
              placeholder="Select client"
              :modal="true">
              <template #modal="{ showModal, query, select, closeModal }">
                <CreateClientModal
                  :query="query"
                  :show="showModal"
                  @close="closeModal"
                  @created="select" />
              </template>
            </DynamicDropdown>
            <p
              v-if="form.errors.client_id"
              class="mt-2 text-sm text-red-600">
              {{ form.errors.client_id }}
            </p>
          </div>
        </div>

        <div class="pt-8 sm:grid sm:grid-cols-3 sm:items-start sm:gap-4">
          <label class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"> Project </label>
          <div class="mt-2 sm:col-span-2 sm:mt-0">
            <DynamicDropdown
              v-model="form.project"
              :search-url="'/api/clients/' + form.client?.id + '/projects?search='"
              :initial-url="'/api/clients/' + form.client?.id + '/projects'"
              :disabled="!form.client?.id"
              :title="!form.client?.id ? 'Please select a client first' : ''"
              placeholder="Select projects"
              :modal="true">
              <template #modal="{ showModal, query, select, closeModal }">
                <CreateProjectModal
                  :query="query"
                  :show="showModal"
                  :client="form.client || {}"
                  @close="closeModal"
                  @created="select" />
              </template>
            </DynamicDropdown>
            <p
              v-if="form.errors.project_id"
              class="mt-2 text-sm text-red-600">
              {{ form.errors.project_id }}
            </p>
          </div>
        </div>

        <hr class="my-8">

        <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4">
          <label class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"> Number of travelers </label>
          <div class="mt-2 sm:col-span-2 sm:mt-0">
            <input
              id="pax"
              v-model="form.pax"
              name="pax"
              type="number"
              min="0"
              oninput="validity.valid||(value='')"
              class="block w-full max-w-lg rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              @change="form.clearErrors('pax')">
            <p
              v-if="form.errors.pax"
              class="mt-2 text-sm text-red-600">
              {{ form.errors.pax }}
            </p>
          </div>
        </div>

        <div class="pt-8 sm:grid sm:grid-cols-3 sm:items-start sm:gap-4">
          <label class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"> Budget per traveller </label>
          <div class="mt-2 sm:col-span-2 sm:mt-0">
            <div class="relative mt-1 flex rounded-md shadow-sm">
              <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <span class="text-gray-500 sm:text-sm">
                  {{ currency ? currency.symbol : '$' }}
                </span>
              </div>
              <input
                id="amount"
                v-model="form.amount"
                type="text"
                name="amount"
                placeholder="0.00"
                autocomplete="username"
                class="block w-full min-w-0 rounded-none rounded-l-md border-gray-300 pl-11 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                @change="form.clearErrors('amount')">
              <span class="flex inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                <CurrencySelectPicker
                  v-model="currency"
                  label=""
                  :borders="false" />
              </span>
            </div>
            <p
              v-if="form.errors.currency_id || form.errors.amount"
              class="mt-2 text-sm text-red-600">
              {{ form.errors.currency_id }}
              {{ form.errors.amount }}
            </p>
          </div>
        </div>

        <hr class="my-8">

        <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4">
          <label class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"> Proposal title </label>
          <div class="mt-2 sm:col-span-2 sm:mt-0">
            <input
              id="title"
              v-model="form.title"
              name="title"
              type="text"
              class="block w-full max-w-lg rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder=""
              @change="form.clearErrors('title')">
            <p
              v-if="form.errors.title"
              class="mt-2 text-sm text-red-600">
              {{ form.errors.title }}
            </p>
          </div>
        </div>

        <div class="pt-8 sm:grid sm:grid-cols-3 sm:items-start sm:gap-4">
          <label class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"> Proposal subtitle </label>
          <div class="mt-2 sm:col-span-2 sm:mt-0">
            <input
              id="subtitle"
              v-model="form.subtitle"
              name="subtitle"
              type="text"
              class="block w-full max-w-lg rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder=""
              @change="form.clearErrors('subtitle')">
            <p
              v-if="form.errors.subtitle"
              class="mt-2 text-sm text-red-600">
              {{ form.errors.subtitle }}
            </p>
          </div>
        </div>

        <div class="pt-8 sm:grid sm:grid-cols-3 sm:items-start sm:gap-4">
          <label class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"> Travel date (from-to) </label>
          <div class="mt-2 sm:col-span-2 sm:mt-0">
            <Datepicker
              v-model="form.range"
              :disabled="!destinationTimezone"
              :title="!destinationTimezone ? 'Please select a destination first' : ''"
              :enable-time-picker="false"
              range
              class="w-72 disabled:cursor-not-allowed"
              multi-calendars
              menu-class-name="p-2"
              :week-numbers="{ type: 'local' }"
              six-weeks="fair"
              partial-flow
              text-input
              :config="{ keepActionRow: true }"
              auto-apply
              :locale="currentLocale"
              :format="'dd.MM.yyyy'" />
            <p
              v-if="form.errors.range"
              class="mt-2 text-sm text-red-600">
              {{ form.errors.range }}
            </p>
          </div>
        </div>

        <div class="pt-8 sm:grid sm:grid-cols-3 sm:items-start sm:gap-4">
          <label class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"> Notes </label>
          <div class="mt-2 sm:col-span-2 sm:mt-0">
            <textarea
              id="notes"
              v-model="form.notes"
              rows="5"
              class="block w-full max-w-lg rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              @change="form.clearErrors('notes')" />
            <p
              v-if="form.errors.notes"
              class="mt-2 text-sm text-red-600">
              {{ form.errors.notes }}
            </p>
          </div>
        </div>

        <hr class="my-8">

        <div class="flex justify-end">
          <CreateButton
            class="mr-2"
            type="submit"
            :disabled="form.processing"
            :class="{'cursor-not-allowed opacity-50': form.processing}" />
          <CancelButton
            class="ml-2"
            @click="back" />
        </div>
      </main>
    </article>
  </form>
</template>

<script setup>
import {
  computed,
  ref, watch,
} from 'vue';
import { useForm } from '@inertiajs/vue3';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import ComboboxWithSecondaryText from '@/Components/Forms/ComboboxWithSecondaryText.vue';
import CurrencySelectPicker from '@/ComponentsDeprecated/Select/CurrencySelectPicker.vue';
import CancelButton from '@/ComponentsDeprecated/Buttons/CancelButton.vue';
import CreateButton from '@/ComponentsDeprecated/Buttons/CreateButton.vue';
import DynamicDropdown from '@/ComponentsDeprecated/Select/DynamicDropdown.vue';
import CreateClientModal from '@/ComponentsDeprecated/Modal/CreateClientModal.vue';
import CreateProjectModal from '@/ComponentsDeprecated/Modal/CreateProjectModal.vue';
import { currentLocale } from '@/utils';

const props = defineProps({
  destinations: {
    type: Array,
    required: true,
  },
});

const availableDestinations = computed(() => props.destinations.map((destination) => ({
  primaryText: destination.name,
  secondaryText: destination.parent?.name ?? destination.area ?? '',
  ...destination,
})).sort(
  (a, b) => (b.name - a.name),
));

const currency = ref({
  flag: null,
  id: 'NOK',
  image: '/img/flags/placeholder.png',
  symbol: 'kr',
});
const projectSelectPicker = ref();
const openModalOnEnter = ref();

const form = useForm({
  client: null,
  project: null,
  title: '',
  subtitle: '',
  range: null,
  notes: '',
  currency: null,
  pax: null,
  amount: null,
  destinations: [],
});

// Get the first destination's timezone
const destinationTimezone = computed(() => {
  if (form.destinations.length > 0) {
    return form.destinations[0].timezone;
  }

  return null;
});

/**
 *
 */
function back() {
  window.history.back();
}

watch(() => form.project, () => {
  if (form.project == null) {
    form.amount = null;
    form.pax = null;
  } else {
    form.amount = form.project.budget_per_traveller;
    form.pax = form.project.total_travelers;
  }
});
watch(() => form.client, () => {
  form.clearErrors('client_id');
});
watch(() => form.project, () => {
  form.clearErrors('project_id');
});
watch(() => form.currency, () => {
  form.clearErrors('currency_id');
});
watch(() => form.range, () => {
  form.clearErrors('range');
});
</script>
